import { apiClient } from '@/api/client'

// ============================================================
// 型定義
// ============================================================

export type MemoListItem = {
  id: number
  title: string
  preview: string
  /** 自分の考えを 1 つ書いたカード */
  is_card: boolean
  created_at: string
  updated_at: string
}

export type MemoListResponse = {
  items: MemoListItem[]
}

export type MemoResponse = {
  id: number
  title: string
  is_card: boolean
  created_at: string
  updated_at: string
}

export type TextDocumentResponse = {
  entity_type: string
  entity_id: number | null
  content_json: string | null
  content_text: string
  updated_at: string | null
  version: number
}

export type TextDocumentVersionResponse = {
  version: number
  updated_at: string | null
}

export type SaveTextDocumentRequest = {
  content_json?: string | null
  content_text?: string
}

/** RFC 6902 JSON Patch オペレーション */
export type JsonPatchOperation = {
  op: 'add' | 'remove' | 'replace' | 'move' | 'copy' | 'test'
  path: string
  value?: unknown
  from?: string
}

export type PatchTextDocumentRequest = {
  patches: JsonPatchOperation[]
  /** 楽観ロック用. 不一致なら 409 */
  expected_version?: number | null
  /** patch 適用後のプレーンテキスト (検索・一覧プレビュー用) */
  content_text?: string
}

// ============================================================
// メモ API
// ============================================================

/** メモ一覧を取得 */
export const getMemos = async (): Promise<MemoListResponse> => {
  const res = await apiClient.get('/memos')
  return res.data
}

/** 新しいメモを作成 */
export const createMemo = async (title = '', isCard = false): Promise<MemoResponse> => {
  const res = await apiClient.post('/memos', { title, is_card: isCard })
  return res.data
}

/** メモのタイトル・カード印を変更（省略した項目は変えない） */
export const updateMemo = async (
  memoId: number,
  request: { title?: string; is_card?: boolean },
): Promise<MemoResponse> => {
  const res = await apiClient.patch(`/memos/${memoId}`, request)
  return res.data
}

/** メモのタイトルを変更 */
export const renameMemo = (memoId: number, title: string): Promise<MemoResponse> => updateMemo(memoId, { title })

/** メモを削除 */
export const deleteMemo = async (memoId: number): Promise<{ message: string }> => {
  const res = await apiClient.delete(`/memos/${memoId}`)
  return res.data
}

// ============================================================
// 汎用テキストドキュメント API
// ============================================================

/** テキストドキュメントを取得 */
export const getTextDocument = async (entityType: string, entityId: number): Promise<TextDocumentResponse> => {
  const res = await apiClient.get(`/memos/documents/${entityType}/${entityId}`)
  return res.data
}

/** テキストドキュメントの version/updated_at のみを取得（変更検知ポーリング用） */
export const getTextDocumentVersion = async (
  entityType: string,
  entityId: number,
): Promise<TextDocumentVersionResponse> => {
  const res = await apiClient.get(`/memos/documents/${entityType}/${entityId}/version`)
  return res.data
}

/** テキストドキュメントを保存 (フル上書き. 過渡期互換用) */
export const saveTextDocument = async (
  entityType: string,
  entityId: number,
  request: SaveTextDocumentRequest,
): Promise<TextDocumentResponse> => {
  const res = await apiClient.put(`/memos/documents/${entityType}/${entityId}`, request)
  return res.data
}

/** テキストドキュメントを JSON Patch で部分更新する. 楽観ロック失敗時は 409 */
export const patchTextDocument = async (
  entityType: string,
  entityId: number,
  request: PatchTextDocumentRequest,
): Promise<TextDocumentResponse> => {
  const res = await apiClient.patch(`/memos/documents/${entityType}/${entityId}`, request)
  return res.data
}

/** 履歴を残した理由 */
export type TextDocumentRevisionReason = 'auto' | 'shrink' | 'overwrite' | 'restore' | 'delete'

export type TextDocumentRevisionItem = {
  id: number
  version: number
  reason: TextDocumentRevisionReason
  preview: string
  text_length: number
  /** この本文が最後に保存された日時 */
  source_updated_at: string | null
  /** 履歴に残した（上書きされた）日時 */
  created_at: string
}

export type TextDocumentRevision = TextDocumentRevisionItem & {
  content_json: string | null
  content_text: string
}

/** 本文の履歴を新しい順に取得 */
export const getTextDocumentRevisions = async (
  entityType: string,
  entityId: number,
): Promise<TextDocumentRevisionItem[]> => {
  const res = await apiClient.get<{ items: TextDocumentRevisionItem[] }>(
    `/memos/documents/${entityType}/${entityId}/revisions`,
  )
  return res.data.items
}

/** 本文の履歴を 1 件、本文つきで取得 */
export const getTextDocumentRevision = async (
  entityType: string,
  entityId: number,
  revisionId: number,
): Promise<TextDocumentRevision> => {
  const res = await apiClient.get(`/memos/documents/${entityType}/${entityId}/revisions/${revisionId}`)
  return res.data
}

/** 本文を履歴の内容に戻す */
export const restoreTextDocumentRevision = async (
  entityType: string,
  entityId: number,
  revisionId: number,
): Promise<TextDocumentResponse> => {
  const res = await apiClient.post(`/memos/documents/${entityType}/${entityId}/revisions/${revisionId}/restore`)
  return res.data
}

// ============================================================
// ノート画像 API
// ============================================================

export type NoteImageResponse = {
  id: number
  /** 配信用の論理URL（例: /api/memos/images/{public_id}/file）。表示時に署名付きURLへ解決する */
  url: string
}

/**
 * Tiptapエディタ本文用の画像をアップロードする。
 *
 * @returns 本文に保存する論理URL（署名なし）
 */
export const uploadNoteImage = async (
  file: File,
  entityType?: string,
  entityId?: number,
  onProgress?: (event: { progress: number }) => void,
  abortSignal?: AbortSignal,
): Promise<string> => {
  const formData = new FormData()
  formData.append('file', file)
  if (entityType) formData.append('entity_type', entityType)
  if (entityId != null) formData.append('entity_id', String(entityId))

  const res = await apiClient.post<NoteImageResponse>('/memos/images', formData, {
    signal: abortSignal,
    onUploadProgress: (event) => {
      if (!onProgress || !event.total) return
      onProgress({ progress: Math.round((event.loaded / event.total) * 100) })
    },
  })
  return res.data.url
}

/**
 * URL から画像をサーバー側でダウンロードして保存する。
 *
 * 失敗時(無効URL / 画像でない / サイズ超過等)は HTTP 400 が返るので、呼び出し側で
 * catch して message を表示すること。
 *
 * @returns 本文に保存する論理URL（署名なし）
 */
export const uploadNoteImageFromUrl = async (
  url: string,
  entityType?: string,
  entityId?: number,
  abortSignal?: AbortSignal,
): Promise<string> => {
  const res = await apiClient.post<NoteImageResponse>(
    '/memos/images/from-url',
    {
      url,
      entity_type: entityType,
      entity_id: entityId,
    },
    { signal: abortSignal },
  )
  return res.data.url
}

export type NoteImageRotateResponse = {
  id: number
  url: string
  /** cache-busting 用の単調増加値（更新時刻 UNIX ミリ秒） */
  revision: number
  size: number
}

/**
 * ノート画像を時計回りに指定角度回転してサーバー上のファイルを書き換える。
 *
 * @param publicId 画像の public_id（論理URL `/api/memos/images/{public_id}/file` から抽出）
 * @param degrees 時計回りの回転角度（90 の倍数）
 * @returns 新しい revision と論理URL
 */
export const rotateNoteImage = async (publicId: string, degrees: number): Promise<NoteImageRotateResponse> => {
  const res = await apiClient.post<NoteImageRotateResponse>(`/memos/images/${publicId}/rotate`, { degrees })
  return res.data
}

// ============================================================
// ノート PDF API
// ============================================================

/** バックエンドの MAX_PDF_BYTES と揃える */
export const MAX_NOTE_PDF_SIZE = 50 * 1024 * 1024

/**
 * Tiptapエディタ本文に埋め込む PDF をアップロードする。
 *
 * @returns 本文に保存する論理URL（例: /api/memos/pdfs/{public_id}/file、署名なし）
 */
export const uploadNotePdf = async (
  file: File,
  onProgress?: (progress: number) => void,
  abortSignal?: AbortSignal,
): Promise<string> => {
  const formData = new FormData()
  formData.append('file', file)

  const res = await apiClient.post<NoteImageResponse>('/memos/pdfs', formData, {
    signal: abortSignal,
    onUploadProgress: (event) => {
      if (!onProgress || !event.total) return
      onProgress(Math.round((event.loaded / event.total) * 100))
    },
  })
  return res.data.url
}

// ============================================================
// Amazon 商品カード API (メモ内埋め込み用)
// ============================================================

/**
 * Amazon 商品カードのスナップショット。
 *
 * 取得時点の情報を保持し、更新はユーザー操作 (再取得ボタン) 経由のみで反映する。
 * Tiptap のカスタムノード属性にそのまま格納する。
 */
export type AmazonProductSnapshot = {
  asin: string | null
  url: string
  title: string
  image: string | null
  price: string | null
  brand: string | null
  rating: number | null
  review_count: number | null
  /** ISO 8601 (UTC) */
  fetched_at: string
}

/** Amazon 商品 URL からスクレイピングでスナップショットを取得する (AI 不使用). */
export const fetchAmazonProduct = async (url: string): Promise<AmazonProductSnapshot> => {
  const res = await apiClient.post<AmazonProductSnapshot>('/memos/amazon-product', { url })
  return res.data
}
